import { novelStoreCreditStorageKey, novelStoreCreditBalanceStorageKey, NOVEL_PASS_CODE_PARAM, } from "@novel/shared/utils/appConstants";
import { getFromLocalStorage, setInLocalStorage, unsetInLocalStorage, } from "@novel/shared/utils/localStorageUtils";
import { storefrontReq } from "@vendor-storefront-global/utils/storefrontReq";
import { isErrorSurfaceable } from "@novel/shared/utils/SurfaceableError";
import { applyDiscountCodeHeadless } from "@vendor-storefront-global/tokenGating/utils/cartUtils/cartOperations/headless/cartOps";
import { getFromParsedUrlHash, removeFromParsedUrlHash } from "@novel/shared/utils/parsedUrlHash";
import { delay } from "@novel/shared/utils/delay";
import { getFromCookies, removeCookie, setCookie } from "@novel/shared/utils/cookieUtils";
import { retryWithBackoff } from "@novel/shared/utils/retryWithBackoff";
import { applyStoreCreditCode } from "@vendor-storefront-global/tokenGating/utils/cartUtils/cartOperations";
export async function autoApplyStoreCredit() {
    var _a, _b, _c, _d;
    const novelStorefrontDataPromise = retryWithBackoff(() => import("@vendor-storefront-global/utils/fetchStorefrontDataFromNovel")).then(({ fetchNovelStorefrontData }) => fetchNovelStorefrontData());
    const onReadyPromise = retryWithBackoff(() => import("@novel/shared/utils/onReady")).then(({ onReady }) => onReady);
    const storeCreditCodeHash = (_a = getFromParsedUrlHash(NOVEL_PASS_CODE_PARAM, false)) === null || _a === void 0 ? void 0 : _a.trim();
    const storeCreditCodeCookie = (_b = getFromCookies(novelStoreCreditStorageKey)) === null || _b === void 0 ? void 0 : _b.trim();
    const storeCreditCodeLocalStorage = (_c = getFromLocalStorage(novelStoreCreditStorageKey)) === null || _c === void 0 ? void 0 : _c.trim();
    // hash precedes cookie precedes local storage
    const novelStoreCreditCode = 
    // we only remove once applying it has been successful
    storeCreditCodeHash || storeCreditCodeCookie || storeCreditCodeLocalStorage;
    if (novelStoreCreditCode !== storeCreditCodeLocalStorage) {
        setInLocalStorage(novelStoreCreditStorageKey, `${novelStoreCreditCode}`);
    }
    if (!novelStoreCreditCode ||
        novelStoreCreditCode === "undefined" ||
        novelStoreCreditCode === "null") {
        return;
    }
    // retrieves balance and whether the code is a discount code
    const storeCreditDisplayResPromise = getStoreCreditDisplayRes(novelStoreCreditCode);
    onReadyPromise.then((onReady) => {
        const listenForDomUpdatePromise = import("@vendor-storefront-global/utils/listenForDomMutations").then(({ listenForDomUpdate }) => listenForDomUpdate);
        onReady(() => {
            // apply dom modifications immediately and upon dom update
            const newDiscountInput = document.createElement("input");
            newDiscountInput.type = "hidden";
            newDiscountInput.name = "discount";
            newDiscountInput.value = novelStoreCreditCode;
            // execute initially and upon DOM updates
            applyDomModifications();
            listenForDomUpdatePromise.then((listenForDomUpdate) => listenForDomUpdate(applyDomModifications));
            function applyDomModifications() {
                // commenting out because giftcard appeared with shoppay
                // hideDynamicCheckoutButtons();
                // add input to forms where it has not already been added
                const cartForms = document.querySelectorAll('form[action="/cart"]');
                cartForms.forEach((cartForm) => {
                    if (!cartForm.querySelector(`input[name="discount"][value="${novelStoreCreditCode}"]`)) {
                        const clonedDiscountInput = newDiscountInput.cloneNode();
                        cartForm.appendChild(clonedDiscountInput);
                    }
                });
            }
        });
    });
    const novelStorefrontData = await novelStorefrontDataPromise;
    const isDiscountImplementation = !!((_d = novelStorefrontData === null || novelStorefrontData === void 0 ? void 0 : novelStorefrontData.integrations) === null || _d === void 0 ? void 0 : _d.some((integration) => integration === "talkable"));
    const toastTriggers = getToastTriggers({
        storeCreditCodeHash,
        isDiscountImplementation,
        storeCreditDisplayResPromise,
        onReadyPromise,
    });
    const { isHeadless, shopifyDomain, storefrontAccessToken } = novelStorefrontData !== null && novelStorefrontData !== void 0 ? novelStorefrontData : {};
    try {
        const useHeadlessFlow = !!isHeadless && !!shopifyDomain && !!storefrontAccessToken;
        if (!useHeadlessFlow) {
            await applyStoreCreditCode({
                isHeadless: useHeadlessFlow,
                storeCreditCode: novelStoreCreditCode,
                onLoading: toastTriggers.onLoading,
            });
        }
        else {
            if (isDiscountImplementation) {
                await applyDiscountCodeHeadless({
                    shopifyDomain,
                    storefrontAccessToken,
                    discountCode: novelStoreCreditCode,
                    onLoading: toastTriggers.onLoading,
                });
            }
            else {
                await applyStoreCreditCode({
                    isHeadless: useHeadlessFlow,
                    storeCreditCode: novelStoreCreditCode,
                    shopifyDomain,
                    storefrontAccessToken,
                    onLoading: toastTriggers.onLoading,
                });
            }
        }
    }
    catch (e) {
        console.error(`Error applying Rewards Balance: ${(e === null || e === void 0 ? void 0 : e.message) || e}`);
        toastTriggers.onError();
        return;
    }
    const storeCreditDisplayRes = await storeCreditDisplayResPromise;
    if (!storeCreditDisplayRes) {
        // response is empty when there is no balance
        return;
    }
    const { storeCreditBalance } = storeCreditDisplayRes;
    // show success toast
    toastTriggers.onSuccess();
    // store the store credit code and balance in local storage
    setInLocalStorage(novelStoreCreditStorageKey, novelStoreCreditCode);
    setInLocalStorage(novelStoreCreditBalanceStorageKey, `${storeCreditBalance}`);
    // for future re-application, remove from hash to avoid showing toast again
    removeFromParsedUrlHash(NOVEL_PASS_CODE_PARAM);
    // also update cookie if it has changed, set it at the highest level domain
    // to handle subdomain handoff (to say a hosted account page from headless storefront)
    if (storeCreditCodeCookie !== novelStoreCreditCode) {
        const date = new Date();
        date.setTime(date.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now
        // apply cookie to highest level domain and all its subdomains
        let domain = window.location.hostname;
        const ResolvedDomain = await retryWithBackoff(() => import("@novel/shared/utils/ResolvedDomain")).then(({ ResolvedDomain }) => ResolvedDomain);
        const resolvedDomain = ResolvedDomain.deconstructUrl(window.location.hostname);
        if (resolvedDomain) {
            domain = resolvedDomain.flattenToStringWithoutSubdomains();
        }
        setCookie({
            name: novelStoreCreditStorageKey,
            value: novelStoreCreditCode,
            expires: date,
            domain,
        });
    }
}
function getToastTriggers({ storeCreditCodeHash, isDiscountImplementation, storeCreditDisplayResPromise, onReadyPromise, }) {
    // if hash is no longer present in the URL, we do not show any toasts
    if (!storeCreditCodeHash) {
        return {
            onLoading: async () => { },
            onSuccess: async () => { },
            onError: async () => { },
        };
    }
    const loadStorefrontStyleSheetPromise = retryWithBackoff(
    // @ts-ignore -- async loading styles
    () => import("@vendor-storefront-global/tokenGating/gateApp/styles/index.scss"));
    // used to handle that the display of the UX of the toast is async
    let completionState = null;
    const loadingToastPromise = import("@novel/shared/utils/toastUtils").then(({ loadingToast, infoToast, errorToast }) => ({ loadingToast, infoToast, errorToast }));
    let _onErrorToast = null;
    let _onSuccessToast = null;
    return {
        onLoading: async () => {
            let storeCreditDisplayRes;
            try {
                storeCreditDisplayRes = await storeCreditDisplayResPromise;
            }
            catch (e) {
                const { errorToast } = await loadingToastPromise;
                errorToast(e);
                return;
            }
            // response is empty when there is no balance
            if (!storeCreditDisplayRes) {
                return;
            }
            const { loadingToast } = await loadingToastPromise;
            await loadStorefrontStyleSheetPromise;
            if (!(await storeCreditDisplayResPromise)) {
                return;
            }
            const onReady = await onReadyPromise;
            onReady(() => {
                const loadingToastResult = loadingToast(isDiscountImplementation
                    ? "Applying Discount Code.."
                    : "Applying Balance to cart...");
                // show loader for at least 2 seconds, otherwise UX is choppy
                const atLeastTwoSeconds = delay(2000);
                _onSuccessToast = (...args) => {
                    atLeastTwoSeconds.then(() => {
                        loadingToastResult.onSuccessToast(...args);
                    });
                };
                _onErrorToast = (...args) => {
                    atLeastTwoSeconds.then(() => {
                        loadingToastResult.onErrorToast(...args);
                    });
                };
                // if the operation has already completed, we still simulate UX of applying
                if ((completionState === null || completionState === void 0 ? void 0 : completionState.type) === "success") {
                    _onSuccessToast(completionState.message);
                }
                else if ((completionState === null || completionState === void 0 ? void 0 : completionState.type) === "error") {
                    _onErrorToast(completionState.message);
                }
            });
        },
        onSuccess: async () => {
            // we will not have shown loader if this comes back empty either
            const storeCreditDisplayRes = await storeCreditDisplayResPromise;
            if (!storeCreditDisplayRes) {
                return;
            }
            const wasCompleted = completionState;
            completionState = {
                type: "success",
                message: isDiscountImplementation
                    ? "Discount code applied to cart!"
                    : `Balance of ${storeCreditDisplayRes.formattedStoreCreditBalance} applied to cart!`,
            };
            if (_onSuccessToast && !wasCompleted) {
                return _onSuccessToast(completionState.message);
            }
        },
        onError: () => {
            const wasCompleted = completionState;
            completionState = {
                type: "error",
                message: "Failed to apply balance, please refresh and try again",
            };
            if (_onErrorToast && !wasCompleted) {
                return _onErrorToast(completionState.message);
            }
        },
    };
}
async function getStoreCreditDisplayRes(novelStoreCreditCode) {
    const storeCreditResponsePromise = storefrontReq.getReq("/api/public/store-credit-balance/:storeCreditCode", {
        params: {
            storeCreditCode: novelStoreCreditCode,
        },
    });
    const storeCreditResponse = await storeCreditResponsePromise;
    if (storeCreditResponse.type === "error") {
        unsetInLocalStorage(novelStoreCreditStorageKey);
        unsetInLocalStorage(novelStoreCreditBalanceStorageKey);
        removeCookie(novelStoreCreditStorageKey);
        if (isErrorSurfaceable(storeCreditResponse.error)) {
            // will show error toast
            throw storeCreditResponse.error;
        }
        // not showing error toast for unhandled error
        console.error(storeCreditResponse.error.message);
        return;
    }
    const { storeCreditBalance, formattedStoreCreditBalance, isDiscountCode } = storeCreditResponse.body;
    if (`${storeCreditBalance}` === "0" && !isDiscountCode) {
        unsetInLocalStorage(novelStoreCreditStorageKey);
        unsetInLocalStorage(novelStoreCreditBalanceStorageKey);
        removeCookie(novelStoreCreditStorageKey);
        removeFromParsedUrlHash(NOVEL_PASS_CODE_PARAM);
        console.info(`No balance for ${novelStoreCreditCode}, not going to apply`);
        return;
    }
    return {
        storeCreditBalance,
        formattedStoreCreditBalance,
        isDiscountCode,
    };
}
